import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@apollo/client';
import { Typography } from '@guildeducationinc/recess';
import { EmployeeContext } from '@src/contexts/EmployeeContextProvider';
import { FUNDING_SUMMARY } from '@src/graphql/queries/benefits';
import { usePaymentRequests } from '@src/hooks/usePaymentRequests';
import { usePrograms } from '@src/hooks/usePrograms';
import { LoaderCircleIcon, useLocale } from 'pe-core-frontend';
import { useContext, useMemo } from 'react';
import { ProgramActivityCard } from './ProgramActivityCard';
export const ProgramActivityCards = () => {
    const { t } = useLocale();
    const { isEligible } = useContext(EmployeeContext);
    // Fetch all programs
    const { programs, loading: loadingPrograms } = usePrograms({
        excludeSingleExpenses: false,
        orderBy: 'updatedAt',
        orderByDir: 'desc',
    });
    // Fetch payment requests with their programs
    const { paymentRequests, loading: loadingPaymentRequests } = usePaymentRequests({
        orderBy: 'updatedAt',
        orderByDir: 'desc',
    });
    // Fetch funding summary for benefit period
    const { data: fundingSummaryData, loading: loadingFundingSummary } = useQuery(FUNDING_SUMMARY, {
        skip: !isEligible,
    });
    const fundingSummary = fundingSummaryData?.fundingSummary;
    // Merge all programs with their payment requests (if any)
    const programsWithPaymentRequests = useMemo(() => {
        // First, group payment requests by program
        const paymentRequestsByProgramId = new Map();
        paymentRequests.forEach(pr => {
            if (!pr.program)
                return;
            const programId = pr.program.id;
            if (!paymentRequestsByProgramId.has(programId)) {
                paymentRequestsByProgramId.set(programId, []);
            }
            paymentRequestsByProgramId.get(programId).push(pr);
        });
        // Then, create an entry for each program (with or without payment requests)
        return programs.map(program => ({
            program,
            paymentRequests: paymentRequestsByProgramId.get(program.id) || [],
        }));
    }, [programs, paymentRequests]);
    const loading = loadingPrograms || loadingPaymentRequests || loadingFundingSummary;
    // Don't render if not eligible and no programs
    if (!isEligible && programsWithPaymentRequests.length === 0) {
        return null;
    }
    // Show loading state when loading and no data yet
    if (loading && programsWithPaymentRequests.length === 0) {
        return (_jsxs("div", { children: [_jsx("div", { className: 'flex justify-between items-center mb-3 mr-3', children: _jsx(Typography, { variant: 'h2', children: t('Activity') }) }), _jsx("hr", { className: 'border-gray-400 mb-4' }), _jsxs(Typography, { variant: 'body2', className: 'text-center py-4', children: [t('Loading activity...'), " ", _jsx(LoaderCircleIcon, { fontSize: 'small' })] })] }));
    }
    // Don't render if no programs
    if (programsWithPaymentRequests.length === 0) {
        return null;
    }
    return (_jsxs("div", { children: [_jsx("div", { className: 'flex justify-between items-center mb-3 mr-3', children: _jsx(Typography, { variant: 'h2', children: t('Activity') }) }), _jsx("hr", { className: 'border-gray-400 mb-4' }), programsWithPaymentRequests.map(({ program, paymentRequests: prs }) => (_jsx(ProgramActivityCard, { program: program, paymentRequests: prs, currency: fundingSummary?.currency }, program.id)))] }));
};
